import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { MAX_SELECT_BRANDS, NewCampaignContext } from "contexts/NewCampaign";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { useContext, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { BentoBrand, SavedBrandStatus } from "schemas/dashboard";

import Collection from "features/Home/Sidebar/FavoritesList/Collections/SingleCollection";
import { trackEvent } from "utils/tracking";

import BrandList from "./BrandList";

export default function SelectCollection() {
  const navigate = useNavigate();
  const {
    collections,
    getCollectionBrands,
    fetchLoading,
    fetchSavedBrandsForCollection,
  } = useContext(SavedBrandCollectionsContext);

  const {
    selectAll,
    setSelectAll,
    selectedBrandIds,
    setSelectedBrandIds,
    selectedCollection,
    setSelectedCollection,
  } = useContext(NewCampaignContext);

  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });
  const loading = fetchLoading === selectedCollection?.id;

  const displayedBrands =
    selectedCollection !== null
      ? getCollectionBrands(
          selectedCollection.id,
          SavedBrandStatus.unsent,
        )?.map((x) => x.bentoBrand)
      : [];

  useEffect(() => {
    if (
      (displayedBrands === undefined || displayedBrands.length === 0) &&
      selectedCollection &&
      !loading
    ) {
      fetchSavedBrandsForCollection(
        selectedCollection?.id,
        SavedBrandStatus.unsent,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCollection?.id]);

  useEffect(() => {
    if (
      isPaginationRefVisible &&
      selectedCollection &&
      displayedBrands &&
      displayedBrands.length < selectedCollection.unsentCount &&
      !loading
    ) {
      fetchSavedBrandsForCollection(
        selectedCollection?.id,
        SavedBrandStatus.unsent,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible, selectedCollection?.id, displayedBrands?.length]);

  const numberSelected = selectAll
    ? Math.min(MAX_SELECT_BRANDS, selectedCollection?.unsentCount || 0)
    : selectedBrandIds.size;

  return (
    <>
      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h6" paragraph>
              Select Collection
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {!selectedCollection && (
              <>
                {Object.values(collections).map((collection) => (
                  <Collection
                    key={collection.id}
                    collection={collections[collection.id]}
                    setSelectedCollection={() => {
                      setSelectedCollection(collection);
                      trackEvent("New Campaign Dialog Selected Collection", {
                        "Collection Id": collection.id,
                        "Collection Name": collection.name,
                      });
                    }}
                  />
                ))}
              </>
            )}
            {selectedCollection && (
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontSize: 14 }}>
                    <IconButton
                      onClick={() => {
                        setSelectedCollection(null);
                        setSelectAll(false);
                        setSelectedBrandIds(new Set());
                      }}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <Box component="i" className="fa-regular fa-arrow-left" />
                    </IconButton>
                    {selectedCollection?.name}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{ my: 1 }}
                >
                  {numberSelected > 0 && (
                    <Tooltip
                      enterTouchDelay={0}
                      title={
                        numberSelected === MAX_SELECT_BRANDS
                          ? `You can only select a maximum of ${MAX_SELECT_BRANDS} brands per campaign`
                          : null
                      }
                    >
                      <Grid item xs="auto">
                        {numberSelected === MAX_SELECT_BRANDS && (
                          <Box
                            component="span"
                            className="fa-solid fa-triangle-exclamation"
                            sx={{ color: "warning.main", mr: 0.5 }}
                          />
                        )}
                        <Typography variant="caption" color="textSecondary">
                          {numberSelected} Selected
                        </Typography>
                      </Grid>
                    </Tooltip>
                  )}
                  {displayedBrands && displayedBrands.length > 0 && (
                    <Grid item xs="auto">
                      <FormControlLabel
                        sx={{ mr: 2 }}
                        control={
                          <Checkbox
                            onChange={() => {
                              setSelectAll(!selectAll);
                              setSelectedBrandIds(new Set());
                            }}
                            checked={selectAll}
                            disableRipple
                            icon={
                              <Box
                                sx={{ color: "green.main" }}
                                component="i"
                                className="fa-regular fa-square"
                              />
                            }
                            checkedIcon={
                              <Box
                                component="i"
                                className="fa-solid fa-square-check"
                                sx={{ color: "green.main" }}
                              />
                            }
                          />
                        }
                        label={<Box sx={{ pr: 1 }}>Select All</Box>}
                        labelPlacement="start"
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid container item xs={12}>
                  <Grid item xs>
                    {(displayedBrands === undefined ||
                      displayedBrands.length > 0) &&
                    selectedCollection.unsentCount !== 0 ? (
                      <BrandList
                        displayedBrands={displayedBrands || []}
                        loading={loading}
                        ref={paginationRef}
                        selectedBrandIds={selectedBrandIds}
                        handleSelect={(bentoBrand: BentoBrand) => {
                          setSelectAll(false);
                          setSelectedBrandIds((prev) => {
                            const copy = new Set(prev);
                            if (copy.has(bentoBrand.id)) {
                              copy.delete(bentoBrand.id);
                            } else {
                              copy.add(bentoBrand.id);
                            }
                            return copy;
                          });
                        }}
                        selectAll={selectAll}
                      />
                    ) : (
                      <Typography variant="caption">
                        <em>
                          You do not have any Unsent brands on this list. Please
                          select a saved list with Unsent brands.
                        </em>
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={() => {
            if (!selectedCollection) {
              navigate("../type");
            } else {
              setSelectedCollection(null);
              setSelectAll(false);
              setSelectedBrandIds(new Set());
            }
          }}
          disableElevation
        >
          Back
        </Button>
        <Button
          disabled={!selectAll && selectedBrandIds.size === 0}
          variant="contained"
          disableElevation
          onClick={() => {
            navigate("../details");
            trackEvent("New Campaign Dialog Selected Collection Brands", {
              "Select All": selectAll,
              "Number Of Brands Selected": selectedBrandIds.size,
            });
          }}
        >
          Next
        </Button>
      </DialogActions>
    </>
  );
}
